<template>
  <section class="project-leave-notification-panel" aria-labelledby="project-leave-notification-heading">
    <div class="notification-heading">
      <div class="notification-heading__title">
        <span class="notification-heading__icon" aria-hidden="true">
          <CIcon name="cil-bell"/>
        </span>
        <div>
          <p class="notification-kicker">PROJECT SLACK CHANNEL</p>
          <h2 id="project-leave-notification-heading">プロジェクトSlack通知先</h2>
          <p>
            このプロジェクトで利用するSlackチャンネルIDと、当日休暇通知の設定を行います。
          </p>
        </div>
      </div>
      <div v-if="settingLoaded" class="notification-heading__badges">
        <CBadge :color="form.notification_flg ? 'success' : 'secondary'">
          休暇 {{ form.notification_flg ? 'ON' : 'OFF' }}
        </CBadge>
        <CBadge :color="hasValidChannel ? 'info' : 'warning'">
          チャンネル {{ hasValidChannel ? '設定済み' : '未設定' }}
        </CBadge>
        <CBadge :color="activeMemberCount > 0 ? 'info' : 'warning'">
          所属者 {{ activeMemberCount }}人
        </CBadge>
      </div>
      <CBadge
        v-else
        class="notification-heading__load-status"
        :color="loadingInitial ? 'info' : 'danger'"
      >
        {{ loadingInitial ? '読込中' : '読込失敗' }}
      </CBadge>
    </div>

    <CAlert
      v-if="errorMessage && settingLoaded"
      color="danger"
      close-button
      @update:show="errorMessage = ''"
    >
      {{ errorMessage }}
    </CAlert>
    <!--
      上の赤帯に出すのは読み込みの失敗だけ。保存・テスト送信の結果はそのボタンの
      すぐ上、休暇通知の切り替えを断った理由は切り替えのすぐ下に出す
      （節の頭からボタンまで約110行あった）。
    -->

    <div v-if="loadingInitial" class="notification-loading">
      <CSpinner color="primary"/>
      <span>プロジェクトSlack通知先を読み込んでいます。</span>
    </div>

    <div v-else-if="!settingLoaded" class="notification-load-failed">
      <CIcon name="cil-warning" size="2xl"/>
      <strong>プロジェクトSlack通知先を表示できませんでした。</strong>
      <p class="notification-load-failed__detail">
        {{ errorMessage || '通信状態と、プロジェクトを管理する権限があるかを確認してください。' }}
      </p>
      <small>再読み込みしても解消しない場合は、表示されているメッセージを添えて管理者へご連絡ください。</small>
      <CButton type="button" color="primary" variant="outline" @click="loadSetting">
        再読み込み
      </CButton>
    </div>

    <template v-else>
      <CAlert color="info">
        共通SlackチャンネルIDは、休暇通知、プロジェクト個別の定期コメント、会社全体通知で共有します。
        会社全体通知は、このIDが有効で所属メンバーが1人以上いる場合に自動で送信対象になります。
      </CAlert>
      <CAlert v-if="!slackFeatureEnabled" color="warning">
        現在のご契約や設定では、Slack通知を利用できません。登録済みチャンネルIDの確認・保存はできます。
      </CAlert>
      <CAlert v-else-if="!slackTokenConfigured" color="warning">
        自社設定にSlack Botトークンがありません。チャンネルIDは保存できますが、通知ONとテスト送信はできません。
      </CAlert>
      <CAlert v-if="activeMemberCount === 0 && hasValidChannel" color="warning">
        有効な所属メンバーが0人のため、休暇通知・個別定期通知・会社全体通知は自動送信されません。
      </CAlert>

      <div class="notification-setting-grid">
        <button
          id="projectLeaveNotificationEnabled"
          type="button"
          class="notification-switch"
          :class="{ 'notification-switch--on': form.notification_flg }"
          role="switch"
          :aria-checked="form.notification_flg ? 'true' : 'false'"
          :disabled="saving || testing"
          @pointerdown.prevent
          @click.prevent.stop="toggleNotification"
        >
          <span class="notification-switch__track" aria-hidden="true">
            <span class="notification-switch__thumb"></span>
          </span>
          <span class="notification-switch__text">
            <strong>当日休暇通知 {{ form.notification_flg ? 'ON' : 'OFF' }}</strong>
            <small>会社営業日の朝に、当日の休暇と承認状態をこのチャンネルへ通知します。時刻は自社設定の就業時間で決まります。</small>
          </span>
        </button>

        <div class="notification-field">
          <label for="projectLeaveSlackChannel">
            共通SlackチャンネルID
            <span v-if="form.notification_flg" class="required-mark">*</span>
          </label>
          <input
            id="projectLeaveSlackChannel"
            v-model="form.slack_channel"
            type="text"
            class="form-control"
            maxlength="50"
            autocomplete="off"
            spellcheck="false"
            placeholder="例：C0123456789"
            :disabled="saving || testing"
            :aria-invalid="channelError ? 'true' : 'false'"
            @input="normalizeChannel"
          >
          <p v-if="channelError" class="notification-field__error" role="alert">
            {{ channelError }}
          </p>
          <small>
            休暇通知がOFFでもチャンネルIDは保持します。会社全体通知は、このIDと有効な所属メンバーから自動判定されます。
          </small>
        </div>
      </div>
      <ActionResultMessage :text="toggleMessage" type="danger" />

      <div class="notification-summary">
        <div>
          <span>当日休暇通知</span>
          <strong>{{ form.notification_flg ? '会社営業日の朝' : 'OFF' }}</strong>
        </div>
        <div>
          <span>会社全体通知</span>
          <strong>{{ hasValidChannel && activeMemberCount > 0 ? '自動送信対象' : '対象外' }}</strong>
        </div>
        <div>
          <span>プロジェクト個別通知</span>
          <strong>下の個別設定を維持</strong>
        </div>
      </div>

      <CAlert color="secondary" class="notification-note">
        非公開チャンネルを含め、通知先でSlack Botを<code>/invite @PREIKAのBot名</code>により参加させてください。
        休暇理由、電話番号、連絡先、備考は通知しません。
      </CAlert>

      <ActionResultMessage :text="actionMessage.text" :type="actionMessage.type" />
      <div class="notification-actions">
        <CButton
          type="button"
          color="primary"
          :disabled="saving || testing"
          @click="saveSetting"
        >
          {{ saving ? '保存しています' : '設定を保存' }}
        </CButton>
        <CButton
          type="button"
          color="secondary"
          variant="outline"
          :disabled="saving || testing || formUnavailable || !hasValidChannel"
          @click="sendTestNotification"
        >
          {{ testing ? '送信しています' : 'テスト送信' }}
        </CButton>
      </div>
    </template>
  </section>
</template>

<script>
import api from '@/services/projectLeaveNotifications'
import { safeErrorDigest } from '../utils/safeResponseMessage'
import ActionResultMessage from '@/components/ActionResultMessage.vue'

const CHANNEL_PATTERN = /^[CG][A-Z0-9]{8,30}$/

export default {
  name: 'ProjectLeaveSlackNotificationSettings',
  components: { ActionResultMessage },
  props: {
    projectId: {
      type: [Number, String],
      required: true
    }
  },
  data () {
    return {
      loadingInitial: true,
      settingLoaded: false,
      saving: false,
      testing: false,
      // 読み込みの失敗だけ（節の頭に出す）。
      errorMessage: '',
      // 保存・テスト送信の結果。そのボタンのすぐ上に出す。
      actionMessage: { type: 'success', text: '' },
      // 休暇通知の切り替えを断った理由。切り替えのすぐ下に出す。
      toggleMessage: '',
      channelError: '',
      slackFeatureEnabled: false,
      slackTokenConfigured: false,
      activeMemberCount: 0,
      form: {
        notification_flg: false,
        slack_channel: ''
      }
    }
  },
  computed: {
    formUnavailable () {
      return !this.slackFeatureEnabled || !this.slackTokenConfigured
    },
    hasValidChannel () {
      return CHANNEL_PATTERN.test(this.normalizedChannel(this.form.slack_channel))
    }
  },
  watch: {
    projectId () {
      this.loadSetting()
    }
  },
  mounted () {
    this.loadSetting()
  },
  methods: {
    async loadSetting () {
      this.loadingInitial = true
      this.settingLoaded = false
      this.clearMessages()
      try {
        this.applyResponse(await api.get(this.projectId))
        this.settingLoaded = true
      } catch (error) {
        this.errorMessage = this.apiError(error, 'プロジェクトSlack通知先を読み込めませんでした。')
      } finally {
        this.loadingInitial = false
      }
    },
    applyResponse (response) {
      const value = response || {}
      this.slackFeatureEnabled = value.slack_feature_enabled === true
      this.slackTokenConfigured = value.slack_token_configured === true
      this.activeMemberCount = this.nonNegativeInteger(value.active_member_count)
      this.form = {
        notification_flg: value.notification_flg === true,
        slack_channel: this.normalizedChannel(value.slack_channel)
      }
    },
    toggleNotification () {
      this.clearMessages()
      const enabling = !this.form.notification_flg
      if (enabling && this.formUnavailable) {
        this.toggleMessage = !this.slackFeatureEnabled
          ? '現在のご契約や設定では、Slack通知を利用できません。'
          : '自社設定でSlack Botトークンを設定してから休暇通知を有効にしてください。'
        return
      }
      const scrollX = window.pageXOffset
      const scrollY = window.pageYOffset
      this.$set(this.form, 'notification_flg', enabling)
      this.$nextTick(() => window.scrollTo(scrollX, scrollY))
    },
    normalizeChannel () {
      this.form.slack_channel = this.normalizedChannel(this.form.slack_channel)
      this.channelError = ''
      this.actionMessage = { type: 'success', text: '' }
    },
    normalizedChannel (value) {
      return String(value || '')
        .replace(/\s/g, '')
        .toUpperCase()
        .slice(0, 50)
    },
    validateForm (testing) {
      this.clearMessages()
      if ((this.form.notification_flg || testing) && this.formUnavailable) {
        this.actionMessage = {
          type: 'danger',
          text: !this.slackFeatureEnabled
            ? '現在のご契約や設定では、Slack通知を利用できません。'
            : '自社設定でSlack Botトークンを設定してください。'
        }
        return false
      }

      const channel = this.normalizedChannel(this.form.slack_channel)
      this.form.slack_channel = channel
      const required = this.form.notification_flg || testing
      if (!channel) {
        if (required) this.channelError = 'SlackチャンネルIDを入力してください。'
        return !required
      }
      if (!CHANNEL_PATTERN.test(channel)) {
        this.channelError = 'SlackチャンネルIDはCまたはGから始まる有効な形式で入力してください。'
        return false
      }
      return true
    },
    payload () {
      return {
        notification_flg: Boolean(this.form.notification_flg),
        slack_channel: this.normalizedChannel(this.form.slack_channel) || null
      }
    },
    async saveSetting () {
      if (!this.validateForm(false)) return
      this.saving = true
      try {
        this.applyResponse(await api.update(this.projectId, this.payload()))
        this.settingLoaded = true
        this.actionMessage = { type: 'success', text: '共通Slackチャンネルと休暇通知の設定を保存しました。休暇通知をOFFにしてもチャンネルIDは保持されます。' }
        this.$emit('updated', {
          project_id: Number(this.projectId),
          source: 'project-channel'
        })
      } catch (error) {
        this.actionMessage = { type: 'danger', text: this.apiError(error, 'プロジェクトSlack通知先を保存できませんでした。') }
      } finally {
        this.saving = false
      }
    },
    async sendTestNotification () {
      if (!this.validateForm(true)) return
      this.testing = true
      try {
        await api.test(this.projectId, this.payload())
        this.actionMessage = { type: 'success', text: '設定中のSlackチャンネルへ接続テストを送信しました。' }
      } catch (error) {
        this.actionMessage = { type: 'danger', text: this.apiError(error, 'テスト送信できませんでした。Botがチャンネルに参加しているかと、チャンネルIDが正しいかを確認してください。') }
      } finally {
        this.testing = false
      }
    },
    nonNegativeInteger (value) {
      const count = Number(value)
      return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
    },
    clearMessages () {
      this.errorMessage = ''
      this.actionMessage = { type: 'success', text: '' }
      this.toggleMessage = ''
      this.channelError = ''
    },
    apiError (error, fallback) {
      const response = error && error.response
      const data = response && response.data
      let message = fallback
      if (typeof data === 'string' && data.trim()) message = data.trim()
      if (data && typeof data.message === 'string' && data.message.trim()) message = data.message.trim()
      // HTTPステータスは画面へ出さない。調査用にはログへ残す。
      if (response && response.status) console.error(`通知先の操作に失敗しました (${response.status})`, safeErrorDigest(error))
      return message
    }
  }
}
</script>

<style scoped>
.project-leave-notification-panel {
  margin-top: 2rem;
  padding: 1.5rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.5rem;
  background: #f8f9fa;
}
.notification-heading,
.notification-heading__title,
.notification-heading__badges,
.notification-loading,
.notification-switch,
.notification-actions {
  display: flex;
}
.notification-heading {
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}
.notification-heading__title { align-items: flex-start; gap: .875rem; }
.notification-heading__badges { flex-wrap: wrap; justify-content: flex-end; gap: .4rem; }
.notification-heading__load-status { flex: 0 0 auto; white-space: nowrap; }
.notification-heading__icon {
  display: inline-flex;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e7f3ff;
  color: #2f6f9f;
}
.notification-heading h2 { margin: 0; font-size: 1.15rem; }
.notification-heading p { margin: .35rem 0 0; color: #5c6873; }
.notification-kicker {
  margin: 0 0 .15rem !important;
  color: #768192 !important;
  font-size: .75rem;
  font-weight: 700;
  letter-spacing: .12em;
}
.notification-loading { min-height: 5rem; align-items: center; gap: .75rem; }
.notification-load-failed {
  display: flex;
  min-height: 5rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .75rem;
  text-align: center;
}
.notification-load-failed__detail {
  max-width: 52rem;
  margin: 0;
  color: #5c6873;
  overflow-wrap: anywhere;
}
.notification-load-failed small { color: #768192; }
.notification-setting-grid {
  display: grid;
  grid-template-columns: minmax(18rem, .9fr) minmax(18rem, 1.1fr);
  gap: 1.25rem;
  align-items: stretch;
}
.notification-switch {
  min-width: 0;
  align-items: center;
  gap: .8rem;
  padding: 1rem;
  border: 1px solid #d8dbe0;
  border-radius: .45rem;
  background: #fff;
  color: #3c4b64;
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
}
.notification-switch--on { border-color: #2eb85c; background: rgba(46, 184, 92, .08); }
.notification-switch__track {
  position: relative;
  width: 3rem;
  height: 1.55rem;
  flex: 0 0 3rem;
  border-radius: 999px;
  background: #9da5b1;
}
.notification-switch--on .notification-switch__track { background: #2eb85c; }
.notification-switch__thumb {
  position: absolute;
  top: .2rem;
  left: .2rem;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .2);
  transition: transform .2s ease;
}
.notification-switch--on .notification-switch__thumb { transform: translateX(1.45rem); }
.notification-switch__text { display: flex; min-width: 0; flex-direction: column; gap: .2rem; }
.notification-switch__text small,
.notification-field small { color: #768192; }
.notification-field {
  padding: 1rem;
  border: 1px solid #d8dbe0;
  border-radius: .45rem;
  background: #fff;
}
.notification-field label { display: block; margin-bottom: .4rem; font-weight: 600; }
.notification-field small { display: block; margin-top: .45rem; line-height: 1.5; }
.notification-field__error { margin: .4rem 0 0; color: #e55353; font-size: .875rem; }
.required-mark { margin-left: .2rem; color: #e55353; }
.notification-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: .65rem;
  margin-top: 1rem;
}
.notification-summary > div {
  display: flex;
  min-height: 4.5rem;
  flex-direction: column;
  justify-content: center;
  padding: .8rem;
  border-radius: .4rem;
  background: #fff;
  text-align: center;
}
.notification-summary span { color: #768192; font-size: .78rem; }
.notification-summary strong { margin-top: .3rem; font-size: .9rem; }
.notification-note { margin-top: 1rem; }
.notification-actions { flex-wrap: wrap; gap: .75rem; margin-top: 1.25rem; }
@media (max-width: 991.98px) {
  .notification-setting-grid { grid-template-columns: 1fr; }
}
@media (max-width: 575.98px) {
  .project-leave-notification-panel { padding: 1rem; }
  .notification-heading { flex-direction: column; }
  .notification-heading__badges { justify-content: flex-start; }
  .notification-summary { grid-template-columns: 1fr; }
  .notification-actions { flex-direction: column; }
  .notification-actions .btn { width: 100%; }
}
</style>
